import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { categoryAPI } from '../../services/api';

interface ProductMedia {
  id: string;
  productname: string;
  createdby: string;
  modifiedby: string | null;
  sequence: number;
  productId: string;
  orderId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
  invoiceId: string | null;
}

interface Group {
  id: string;
  name: string;
  isprice: boolean;
  noofusers: number;
  categoryId: string | null;
  status: boolean;
  createdby: string;
  modifiedby: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
}

interface GroupPrice {
  id: string;
  newprice: number;
  minimumorderquantity: number;
  groupId: string;
  productId: string;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
  group: Group;
}

interface ProductDetail {
  id: number;
  lable: string;
  data: string;
}

interface Category {
  id: string;
  name: string;
  categorystatus: number;
  totalproducts: number;
  totalsubcategories: number;
  createdby: string;
  modifiedby: string | null;
  parentId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  groupcategoryId: string | null;
  masterdetailId: string;
}

interface Product {
  id: string;
  name: string;
  productno: number;
  price: number;
  availablequantity: number;
  productstatus: number;
  inInquiry: boolean;
  expecteddays: number | null;
  createdby: string;
  modifiedby: string;
  sellcounter: number;
  categoryId: string;
  userId: string;
  deletedAt: string | null;
  created: string;
  modified: string;
  productbrandId: string | null;
  other: any;
  description: string;
  productdetails: string;
  producttypeId: number | null;
  videourllink: string | null;
  productvariation: any;
  variationconfig: string;
  productunit: string | null;
  productbrochure: string | null;
  masterdetailId: string;
  lastModifiedPriceDate: string;
  categoryName: string;
  pricemode: boolean;
  minimumorderquantity: number;
  category: Category;
  productmedia: ProductMedia[];
  groupprices: GroupPrice[];
}

interface ProductViewModalProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
}

const ProductViewModal: React.FC<ProductViewModalProps> = ({
  open,
  onClose,
  product,
}) => {
  const [selectedImage, setSelectedImage] = useState<{ url: string, index: number } | null>(null);

  if (!product) return null;

  // Debug image URL generation
  const imageUrl = product.productmedia?.[0]?.productname
    ? categoryAPI.getMediaDownloadUrl(product.productmedia[0].productname)
    : undefined;

  console.log('Modal image debug:', {
    productName: product.name,
    productMedia: product.productmedia,
    imageUrl: imageUrl,
    filename: product.productmedia?.[0]?.productname
  });

  // Parse product details JSON string
  const parsedProductDetails = (() => {
    try {
      const parsed = JSON.parse(product.productdetails);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: '100%',
          width: '880px', // reduced width for better readability
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          m: 0,
          borderBottom: '1px solid #e2e8f0',
          // removed sticky positioning
          background: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: .4 }}>
            Product Details
          </Typography>
        </Box>
        <Button onClick={onClose} sx={{ color: 'error.main', minWidth: 'auto', p: 0.5, '&:hover': { bgcolor: 'error.light' } }}>
          <CloseIcon sx={{ fontSize: '1.2rem' }} />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ m: 0, maxHeight: '85vh', overflow: 'auto', p: 2 }}>
        {/* Sticky In-Content Header (name + chips) */}
        <Box
          sx={{
            // removed sticky positioning so it scrolls away
            pt: 1,
            pb: 1.5,
            mb: 2,
            borderBottom: '1px solid #e5e7eb'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={imageUrl}
              alt={product.name}
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'primary.main',
                fontSize: '1.15rem',
                fontWeight: 700,
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              {product.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: .25, lineHeight: 1.1 }} noWrap>
                {product.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: .75, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  label={product.productstatus === 1 ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '.625rem',
                    fontWeight: 600,
                    bgcolor: product.productstatus === 1 ? 'success.light' : 'error.light',
                    color: product.productstatus === 1 ? 'success.dark' : 'error.dark',
                    borderRadius: 1.5,
                  }}
                />
                <Chip
                  label={`${product.price} pts`}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '.625rem',
                    fontWeight: 600,
                    bgcolor: 'grey.100',
                    color: 'text.secondary',
                    borderRadius: 1.5,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Product Images Section */}
        {Array.isArray(product.productmedia) && product.productmedia.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: .8, color: 'primary.main' }}>
                Product Images ({product.productmedia.length})
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedImage({ url: categoryAPI.getMediaDownloadUrl(product.productmedia[0].productname), index: 1 })}
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, fontSize: '.65rem', px: 1.5, py: .25 }}
              >
                See All
              </Button>
            </Box>
            <Box sx={{ display: 'flex', overflowX: 'auto', gap: 1, py: .5, pr: .5, '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-thumb': { background: '#d1d5db', borderRadius: 4 } }}>
              {product.productmedia.map((media, index) => {
                const imageUrl = categoryAPI.getMediaDownloadUrl(media.productname);
                return (
                  <Box
                    key={media.id}
                    onClick={() => setSelectedImage({ url: imageUrl, index: index + 1 })}
                    sx={{
                      position: 'relative',
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      overflow: 'hidden',
                      flexShrink: 0,
                      cursor: 'pointer',
                      border: '1px solid #e2e8f0',
                      bgcolor: 'grey.50',
                      '&:hover': { borderColor: 'primary.main', boxShadow: '0 0 0 2px rgba(25,118,210,0.25)' }
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={`Product ${index + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Box sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,.55)', color: '#fff', width: 16, height: 16, fontSize: '0.55rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
                      {index + 1}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Helper for repeating blocks */}
        {/** Basic Info Block */}
        <Box sx={{ background: '#fafafa', p: 1.5, borderRadius: 2, mb: 2 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: .8, color: 'primary.main' }}>Basic Information</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: .75, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .5, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .25 }}>Product Number</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '.9rem' }}>{product.productno}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .5, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .25 }}>Category</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '.9rem' }}>{product.category?.name || product.categoryName}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .5, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .25 }}>Available Qty</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '.9rem' }}>{product.availablequantity}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .5, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .25 }}>Min Order Qty</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '.9rem' }}>{product.minimumorderquantity}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .5, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .25 }}>Sell Counter</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '.9rem' }}>{product.sellcounter}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Description Block */}
        <Box sx={{ background: '#fafafa', p: 1.5, borderRadius: 2, mb: 2 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: .8, color: 'primary.main' }}>Description</Typography>
          <Box
            sx={{ mt: 1 }}
            // Render HTML content safely
            dangerouslySetInnerHTML={{
              __html: product.description || '<span style="color:#888">No description available</span>',
            }}
          />
        </Box>

        {/* Product Details Block */}
        {Array.isArray(parsedProductDetails) && parsedProductDetails.length > 0 && (
          <Box sx={{ background: '#fafafa', p: 1.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: .8, color: 'primary.main' }}>Product Details</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: .75, mt: 1 }}>
              {parsedProductDetails.map((detail: ProductDetail) => (
                <Box key={detail.id} sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .5, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .25 }}>{detail.lable}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: '.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default' }}
                      title={detail.data}
                    >
                      {detail.data}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Group Detail Info Block */}
        {Array.isArray(product.groupprices) && product.groupprices.length > 0 && (
          <Box sx={{ background: '#fafafa', p: 1.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: .8, color: 'primary.main' }}>Group Detail Info ({product.groupprices.length})</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {product.groupprices.map((gp: GroupPrice) => (
                <Box key={gp.id} sx={{ display: 'flex', gap: 1, background: 'white', border: '1px solid #e2e8f0', borderRadius: 1.5, p: .75 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .5, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .25 }}>Group Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '.9rem' }}>{gp.group ? gp.group.name : 'N/A'}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .5, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .25 }}>Points</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '.9rem' }}>{gp.newprice}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Image Preview Modal */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            backgroundColor: 'white',
            maxWidth: '500px',
            maxHeight: '400px',
          },
        }}
      >
        <DialogContent sx={{ p: 0, m: 0, position: 'relative' }}>
          <Box sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '350px',
            p: 2
          }}>
            {/* Close button */}
            <IconButton
              onClick={() => setSelectedImage(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                zIndex: 10,
                width: '32px',
                height: '32px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                }
              }}
            >
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </IconButton>

            {/* Previous button */}
            {selectedImage && selectedImage.index > 1 && (
              <IconButton
                onClick={() => {
                  const prevIndex = selectedImage.index - 1;
                  const prevMedia = product.productmedia[prevIndex - 1];
                  const prevUrl = categoryAPI.getMediaDownloadUrl(prevMedia.productname);
                  setSelectedImage({ url: prevUrl, index: prevIndex });
                }}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  zIndex: 10,
                  width: '32px',
                  height: '32px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)',
                  }
                }}
              >
                <Box component="span" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>‹</Box>
              </IconButton>
            )}

            {/* Next button */}
            {selectedImage && selectedImage.index < product.productmedia.length && (
              <IconButton
                onClick={() => {
                  const nextIndex = selectedImage.index + 1;
                  const nextMedia = product.productmedia[nextIndex - 1];
                  const nextUrl = categoryAPI.getMediaDownloadUrl(nextMedia.productname);
                  setSelectedImage({ url: nextUrl, index: nextIndex });
                }}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  zIndex: 10,
                  width: '32px',
                  height: '32px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)',
                  }
                }}
              >
                <Box component="span" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>›</Box>
              </IconButton>
            )}

            {/* Image */}
            {selectedImage && (
              <Box sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img
                  src={selectedImage.url}
                  alt={`Product ${selectedImage.index}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    console.log('Preview image load error for:', selectedImage.url);
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ProductViewModal;